import React from 'react';
import { PETS, resolvePetId } from '../../data/pets';

/**
 * A pet drawn from its generated art rather than hand-built SVG.
 *
 * Every species now has a voxel-chibi illustration (see
 * petlings-ios-app/scripts/pet-art): the same art the iOS app ships, so a
 * pet looks the same on the web, on the phone and in the widget. The PNG is
 * the default because the art is shaded; the traced SVG is there for anywhere
 * a vector is wanted. Each species also has six expressions, which the
 * renderer picks by mood.
 */
/** Every species has art: the registry is the list. */
export const ART_PETS = new Set(PETS.map((p) => p.id));

export function artIdFor(petTypeId) {
  const id = resolvePetId(petTypeId);
  return ART_PETS.has(id) ? id : null;
}

export function artUrl(petTypeId, format = 'png') {
  const id = artIdFor(petTypeId);
  return id ? `/pets/art/${format}/${id}.${format}` : null;
}

/** Faces drawn for every species, in public/pets/art/moods/. */
const EXPRESSIONS = new Set(['happy', 'energetic', 'sleepy', 'sad', 'hungry', 'love']);
/** Expressions that change the pose, so cosmetics placed on the base no longer fit. */
const POSE_CHANGES = new Set(['energetic', 'sleepy']);

/**
 * The clip a mood holds, as in the app (PetArt.Clip.clip(for:)): a pet in
 * good spirits idles, a sleepy one curls up, a sad or hungry one droops.
 * Each is a loop in public/pets/art/anim/. A mood not here is a still face.
 */
const MOOD_CLIPS = { happy: 'idle', content: 'idle', neutral: 'idle', sleepy: 'sleep', sad: 'sad', hungry: 'sad' };

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function makeArtPet(petTypeId) {
  const id = artIdFor(petTypeId);
  const base = `/pets/art/png/${id}.png`;
  // `clip` is something the pet was asked to do (a wave on a tap); it plays
  // once, in front of the mood, and `clipKey` changing plays it again.
  function ArtPet({ size = 200, className, mood, wearsCosmetics = false, color, clip, clipKey = 0 }) {
    // A pet moves as in the app: a clip in place of the still, where it is
    // big enough to see, bare (a hat cannot follow a head tilt baked into
    // the clip), and motion is welcome.
    const moves = !wearsCosmetics && size >= 140 && !prefersReducedMotion();
    const moodClip = MOOD_CLIPS[mood || 'neutral'];
    const playing = moves ? (clip || moodClip) : null;
    const face = EXPRESSIONS.has(mood) && !(wearsCosmetics && POSE_CHANGES.has(mood));
    const src = playing ? `/pets/art/anim/${id}_${playing}.webp` : face ? `/pets/art/moods/${id}_${mood}.png` : base;
    const img = (
      <img
        key={`${src}#${clip ? clipKey : 0}`}
        src={src}
        width={size}
        height={size}
        className={className}
        alt=""
        draggable={false}
        loading="lazy"
        onError={(e) => { if (e.currentTarget.src !== window.location.origin + base) e.currentTarget.src = base; }}
        style={{ objectFit: 'contain', display: 'block', userSelect: 'none' }}
      />
    );
    if (!color) return img;
    // A tint on the pet only: a colour layer masked to the picture's own
    // outline, where the old overlay tinted a circle round it, background
    // and all.
    const mask = `url(${src}) center / contain no-repeat`;
    return (
      <span style={{ position: 'relative', display: 'block', width: size, height: size }}>
        {img}
        <span
          aria-hidden="true"
          style={{
            position: 'absolute', inset: 0, background: color, mixBlendMode: 'color', opacity: 0.55,
            WebkitMask: mask, mask, pointerEvents: 'none',
          }}
        />
      </span>
    );
  }
  ArtPet.displayName = `ArtPet(${petTypeId})`;
  return ArtPet;
}
